import { Link, Outlet, useLocation } from "@tanstack/react-router"
import { IconChevronLeftOutline18 as ChevronLeft } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { Button } from "@productlane/ui"

import { spotlightAnchorRef } from "@/components/ai-widget-launcher"
import { CommandMenu } from "@/components/cmdk"
import { DetailDrawer } from "@/components/detail-drawer"
import { ErrorBoundary } from "@/components/error-boundary"
import { OutOfOfficeDialog } from "@/components/out-of-office/out-of-office-dialog"
import { useLockDocumentScroll } from "@/lib/hooks/use-lock-document-scroll"
import {
	FLOATING_BACK_LINK_SCRIM_CLASS,
	INLINE_BACK_LINK_FADE_CLASS,
	settingsBackLinkPlacement,
} from "./settings-back-link-placement"
import {
	SETTINGS_MAIN_CURSOR_CLASS,
	settingsColumnFor,
} from "./settings-column"
import { findParentNavItem } from "./settings-parent-nav"
import {
	SettingsRightRailSlot,
	useHasSettingsRightRail,
} from "./settings-right-rail"
import {
	SETTINGS_PAGE_PADDING_X_CLASS,
	SettingsCardInsetContext,
} from "./settings-switches/settings-container"
import { SettingsSidebar } from "./sidebar"

export const SettingsLayout = () => {
	const { pathname } = useLocation()
	const column = settingsColumnFor(pathname)
	const parentNavItem = findParentNavItem(pathname)
	const hasRightRail = useHasSettingsRightRail()
	useLockDocumentScroll()
	const backLinkPlacement = settingsBackLinkPlacement({
		column,
		hasRightRail,
	})

	const backLink = parentNavItem ? (
		<Link to={parentNavItem.href}>
			<Button variant="ghost" Icon={ChevronLeft}>
				{parentNavItem.title}
			</Button>
		</Link>
	) : null

	// A top-level page leans on the sidebar to get back, which a phone lacks, so
	// there the trail continues up to the settings list at /settings.
	const phoneBackLink =
		parentNavItem || pathname === "/settings" ? null : (
			<Link to="/settings">
				<Button variant="ghost" Icon={ChevronLeft}>
					Settings
				</Button>
			</Link>
		)

	return (
		<>
			<CommandMenu />
			<OutOfOfficeDialog />
			{/* cmd+K opens issues and projects in the drawer, so it has to be
			    mounted anywhere the palette is. */}
			<ErrorBoundary boundary="drawer" resetOnNavigate>
				<DetailDrawer />
			</ErrorBoundary>
			{/* Chrome reads labels like General and Personal as German and mangles them; settings is all UI copy. */}
			<div className="flex" translate="no">
				<SettingsSidebar
					content={
						<div className="relative flex h-full flex-1 flex-col">
							{backLink && (
								<div className={backLinkPlacement.floating}>
									<div className={FLOATING_BACK_LINK_SCRIM_CLASS} />
									<div className="relative">{backLink}</div>
								</div>
							)}
							<div className="flex min-h-0 flex-1">
								<div
									id="settings-scroll-container"
									className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg"
								>
									{backLink && (
										<div className={backLinkPlacement.inline}>
											{backLink}
											<div className={INLINE_BACK_LINK_FADE_CLASS} />
										</div>
									)}
									{phoneBackLink && (
										<div className={cn(backLinkPlacement.inline, "sm:hidden")}>
											{phoneBackLink}
											<div className={INLINE_BACK_LINK_FADE_CLASS} />
										</div>
									)}
									<main
										ref={spotlightAnchorRef}
										className={cn(
											"mx-auto flex w-full shrink-0 grow flex-col px-5 py-8",
											SETTINGS_MAIN_CURSOR_CLASS,
											SETTINGS_PAGE_PADDING_X_CLASS,
											column === "full" ? "sm:py-10" : "sm:py-16",
											"pb-8 sm:pb-28",
											// Column width plus the side padding, so content keeps its width.
											column === "full"
												? "max-w-none"
												: column === "extraWide"
													? "max-w-[1212px]"
													: column === "wide"
														? "max-w-[928px]"
														: "max-w-[704px]",
										)}
									>
										<ErrorBoundary boundary="settings" resetOnNavigate>
											<SettingsCardInsetContext.Provider value={true}>
												<Outlet />
											</SettingsCardInsetContext.Provider>
										</ErrorBoundary>
									</main>
								</div>
								<SettingsRightRailSlot />
							</div>
						</div>
					}
				/>
			</div>
		</>
	)
}
